// components/pages/FullFeedPage.js - committee API support, back button fix,
// and filter-bar changes that load data at every layer of the cascade.
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import FeedPageLayout from './FeedPageLayout';
import { useFeedPageData } from '../../hooks/useFeedPageData';
import { useFeedPageParams } from '../../hooks/useFeedPageParams';
import useCollections from '../../hooks/useCollections';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';

// Which state (if any) the current feed belongs to. Table names alone can't
// always answer this — several states share a table and differ only by a filter
// value — so the selection is carried in the URL and handed to FeedFilterBar as
// the authoritative `stateKey`. Its own detection is then only a fallback for
// links that predate this param.
const STATE_PARAM = 'ggState';

const FullFeedPage = () => {
  const { schema, table } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [feedLength, setFeedLength] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState('');

  // State for URL-based filter parameters
  const [urlFilterColumn, setUrlFilterColumn] = useState('');
  const [urlFilterString, setUrlFilterString] = useState('');
  const [urlOrderBy, setUrlOrderBy] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Committee API specific state
  const [isCommitteeAPI, setIsCommitteeAPI] = useState(false);
  const [committeeName, setCommitteeName] = useState('');

  // Track back/forward navigation
  const [isPopstateNavigation, setIsPopstateNavigation] = useState(false);

  // Which state the filter bar should show as selected. Read straight off the
  // URL so back/forward and shared links stay correct.
  const filterStateKey = useMemo(
    () => new URLSearchParams(location.search).get(STATE_PARAM) || null,
    [location.search]
  );

  // Listen for popstate (back/forward button)
  useEffect(() => {
    const handlePopstate = () => {
      setIsPopstateNavigation(true);
    };

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, []);

  // Parse URL parameters on mount and location change
  useEffect(() => {
    // Skip if this is a back/forward navigation
    if (isPopstateNavigation) {
      // Still update the UI state from URL, just don't trigger new searches
      const searchParams = new URLSearchParams(location.search);
      const query = searchParams.get('query');

      if (query) {
        const decodedQuery = decodeURIComponent(query);
        setSearchQuery(decodedQuery);
        setSearchPerformed(true);
      } else {
        setSearchQuery('');
        setSearchPerformed(false);
      }

      setIsPopstateNavigation(false);
      return;
    }

    // Don't run URL parsing if we just manually updated the URL
    if (Date.now() - (window.lastManualUrlUpdate || 0) < 100) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    // Check for committee API parameters
    const useCommitteeAPI = searchParams.get('useCommitteeAPI') === 'true';
    const committee = searchParams.get('committee');

    if (useCommitteeAPI && committee) {
      setIsCommitteeAPI(true);
      setCommitteeName(decodeURIComponent(committee));
    } else {
      setIsCommitteeAPI(false);
      setCommitteeName('');
    }

    // Extract filter parameters from URL
    const filterColumn = searchParams.get('filterColumn');
    const filterString = searchParams.get('filterString');
    const orderBy = searchParams.get('orderBy');
    const query = searchParams.get('query');

    // Set URL filter parameters
    if (filterColumn && filterString) {
      setUrlFilterColumn(filterColumn);
      setUrlFilterString(decodeURIComponent(filterString));
    } else {
      setUrlFilterColumn('');
      setUrlFilterString('');
    }

    // Set URL order by parameter
    if (orderBy) {
      setUrlOrderBy(orderBy);
    } else {
      setUrlOrderBy('');
    }

    // Handle search query from URL
    if (query) {
      const decodedQuery = decodeURIComponent(query);
      if (decodedQuery !== searchQuery || !isInitialized) {
        setSearchQuery(decodedQuery);
        setSearchPerformed(true);
      }
    } else {
      if (!isInitialized) {
        setSearchQuery('');
        setSearchPerformed(false);
      } else if (location.search === '' || !searchParams.has('query')) {
        setSearchQuery('');
        setSearchPerformed(false);
      }
    }

    setIsInitialized(true);

  }, [location.search, isInitialized, isPopstateNavigation]);

  // A new table means a new feed: start the page size over rather than asking
  // the API for 60 rows of something the user just switched to.
  useEffect(() => {
    setFeedLength(20);
    setSelectedAgency('');
  }, [schema, table]);

  // Custom hooks
  const { orderBy, filterColumn, filterString, tableConfig } = useFeedPageParams(table);

  // Use URL parameters if available, otherwise use default parameters
  const effectiveOrderBy = urlOrderBy || orderBy || 'created_at';
  const effectiveFilterColumn = urlFilterColumn || filterColumn;
  const effectiveFilterString = urlFilterString || filterString;

  // Use the existing useFeedPageData hook which now handles committee API
  const { data, loading, error } = useFeedPageData(
    schema,
    table,
    feedLength,
    searchQuery,
    searchPerformed,
    effectiveOrderBy,
    selectedAgency,
    effectiveFilterColumn,
    effectiveFilterString
  );

  const collectionHook = useCollections();

  // Handle search from integrated nav bar
  const handleIntegratedSearch = useCallback((query) => {
    const trimmedQuery = query.trim();
    const isSearching = !!trimmedQuery;

    // Update state in a batch to prevent race conditions
    React.startTransition(() => {
      setSearchQuery(trimmedQuery);
      setSearchPerformed(isSearching);

      // Reset feed length when starting a new search
      if (isSearching && !searchPerformed) {
        setFeedLength(20);
      }

      // Clear agency filter when searching
      if (isSearching) {
        setSelectedAgency('');
      }
    });

    // Update URL to include search query while preserving committee params
    window.lastManualUrlUpdate = Date.now();
    const searchParams = new URLSearchParams(location.search);

    if (!isSearching) {
      // Remove query param but keep other params
      searchParams.delete('query');
    } else {
      // Add/update query param
      searchParams.set('query', encodeURIComponent(trimmedQuery));
    }

    const newUrl = searchParams.toString()
      ? `${location.pathname}?${searchParams.toString()}`
      : location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [location.pathname, location.search, searchPerformed]);

  // The global bottom search bar dispatches this event on /feed pages so it can
  // search this page's data directly (rather than navigating to /search).
  useEffect(() => {
    const handler = (e) => handleIntegratedSearch((e.detail && e.detail.query) || '');
    window.addEventListener('gg:feed-search', handler);
    return () => window.removeEventListener('gg:feed-search', handler);
  }, [handleIntegratedSearch]);

  // Event handlers
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    handleIntegratedSearch(value);
  }, [handleIntegratedSearch]);

  const handleAgencySelect = useCallback((agency) => {
    setSelectedAgency(agency);
  }, []);

  // Handle load more
  const handleSearchLoadMore = useCallback(() => {
    setFeedLength(prev => prev + 20);
  }, []);

  // Clear URL filters function
  const handleClearFilters = useCallback(() => {
    // navigate(), not history.pushState(): pushState is invisible to the router,
    // so location.search stayed stale and anything derived from it (the state
    // param the filter bar reads, for one) kept the value we just cleared.
    navigate(`/feed/${schema}/${table}`);
    setUrlFilterColumn('');
    setUrlFilterString('');
    setUrlOrderBy('');
    setSelectedAgency('');
    setSearchQuery('');
    setSearchPerformed(false);
    setIsCommitteeAPI(false);
    setCommitteeName('');
  }, [navigate, schema, table]);

  // Check if we have active filters
  const hasActiveFilters = !!(
    effectiveFilterColumn && effectiveFilterString ||
    selectedAgency ||
    searchPerformed ||
    isCommitteeAPI
  );

  // Generate filter info for display
  const activeFilterInfo = useMemo(() => {
    const filters = [];

    if (isCommitteeAPI && committeeName) {
      filters.push({
        type: 'Committee Filter',
        label: `Committee: ${committeeName}`,
        value: committeeName
      });
    }

    if (effectiveFilterColumn && effectiveFilterString) {
      filters.push({
        type: 'URL Filter',
        label: `${effectiveFilterColumn}: ${effectiveFilterString}`,
        value: effectiveFilterString
      });
    }

    if (selectedAgency) {
      filters.push({
        type: 'Agency Filter',
        label: `Agency: ${selectedAgency}`,
        value: selectedAgency
      });
    }

    if (searchPerformed && searchQuery) {
      filters.push({
        type: 'Search Filter',
        label: `Search: ${searchQuery}`,
        value: searchQuery
      });
    }

    return filters;
  }, [isCommitteeAPI, committeeName, effectiveFilterColumn, effectiveFilterString, selectedAgency, searchPerformed, searchQuery]);

  // Helper function to format table name for display
  const formatTableName = useCallback((tableName) => {
    // Special case for committee materials with committee filter
    if (tableName === 'committee_materials' && committeeName) {
      return `${committeeName} Materials`;
    }

    return tableName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, [committeeName]);

  // Prepare props for layout
  const layoutProps = {
    country: schema,
    searchQuery,
    onSearchChange: handleSearchChange,
    selectedAgency,
    onAgencySelect: handleAgencySelect,
    hasAgencyFilter: tableConfig?.hasAgencyFilter || false,
    buttonName: tableConfig?.buttonName || 'Document',
    feedName: tableConfig?.feedName || formatTableName(table),
    branch: tableConfig?.branch || '',
    topic: tableConfig?.topic || '',
    data,
    loading,
    error,
    schema,
    table,
    onLoadMore: handleSearchLoadMore,
    hasActiveFilters,
    activeFilterInfo,
    onClearFilters: handleClearFilters,
    effectiveParameters: {
      orderBy: effectiveOrderBy,
      filterColumn: effectiveFilterColumn,
      filterString: effectiveFilterString,
      isCommitteeAPI,
      committeeName
    },
    collectionProps: {
      isModalOpen: collectionHook.isModalOpen,
      currentItem: collectionHook.currentItem,
      saveSuccess: collectionHook.saveSuccess,
      note: collectionHook.note,
      setNote: collectionHook.setNote,
      handleSave: collectionHook.handleSave,
      closeModal: collectionHook.closeModal,
      isStorageAvailable: collectionHook.isStorageAvailable,
    }
  };

  // Handle sort change from filter bar
  const handleSortChange = useCallback((fieldKey) => {
    window.lastManualUrlUpdate = Date.now();
    setUrlOrderBy(fieldKey);
    setFeedLength(20);

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('orderBy', fieldKey);
    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  }, [location.pathname, location.search]);

  // Handle filter bar changes (country / level / topic / feed).
  //
  // FeedFilterBar now resolves the rest of the cascade itself, so a Country or
  // Level pick arrives here already carrying a concrete feed. That's what makes
  // the bar feel live: every layer loads data instead of the first three being
  // no-ops until the user reaches the Feed dropdown.
  //
  // Navigation is navigate(), not window.location.href. The old full page
  // reload threw away the React tree, the data cache and the scroll position on
  // every filter change, which is most of why this felt sluggish.
  const handleFilterChange = useCallback(({ schema: newSchema, stateKey: newStateKey, feed }) => {
    // Topics the page owns rather than the feed config (Glance / Recents /
    // Following / federal Updates) have no table to load here. Leave the
    // current feed on screen rather than navigating somewhere arbitrary.
    if (!feed || !feed.apiEndpoint) return;

    const feedSchema = newSchema || schema;
    const params = new URLSearchParams(feed.apiEndpoint.replace(/^&/, ''));

    if (feed.useCommitteeAPI) {
      const committee = params.get('committee');
      const chamber = params.get('chamber');
      if (!committee) return;
      // The committee value is deliberately encoded here and decoded twice on
      // the way back in (URLSearchParams.get, then decodeURIComponent in the
      // parse effect above). Other callers in the app build committee links the
      // same way, so this stays as-is until they all change together.
      const newUrl = `/feed/${feedSchema}/committee_materials?useCommitteeAPI=true&committee=${encodeURIComponent(committee)}${chamber ? `&chamber=${chamber}` : ''}`;
      navigate(newUrl);
      return;
    }

    const feedTable = params.get('table') || params.get('table_name');
    if (!feedTable) return;

    const urlParams = new URLSearchParams();
    const filterCol = params.get('filter_column');
    const filterStr = params.get('filter_string');
    if (filterCol && filterStr) {
      urlParams.set('filterColumn', filterCol);
      urlParams.set('filterString', filterStr);
    }
    const feedOrderBy = params.get('order_by');
    if (feedOrderBy) {
      urlParams.set('orderBy', feedOrderBy);
    }
    // Carry the state selection forward. Two states can share a table, so the
    // table name alone can't tell the filter bar which state it's showing.
    if (newStateKey) {
      urlParams.set(STATE_PARAM, newStateKey);
    }

    const qs = urlParams.toString();
    navigate(qs ? `/feed/${feedSchema}/${feedTable}?${qs}` : `/feed/${feedSchema}/${feedTable}`);
  }, [navigate, schema]);

  return (
    <>
      {/* Integrated Search Navigation Bar.
          It owns its own search box (SearchContext plus the gg:feed-search
          event this page listens for), so no search props are passed down —
          the searchQuery / onSearchChange / placeholder props this used to send
          were never read.

          hideLocationPill drops the flag/region pill from the left of the bar
          on this page only. The filter bar's own controls are the country
          statement here, and the pill was taking roughly a fifth of the row on
          mobile — which is why Level / Topic / Feed were truncating to "Fe",
          "Legisl", "Bill Up". Note the tradeoff: the pill is also how the
          country gets switched, and this page passes hideCountry to
          FeedFilterBar, so there is no country switcher on /feed at all now.
          Drop `hideCountry` in IntegratedSearchNavBar (or pass a flag through)
          if the Country dropdown should come back into the filter bar. */}
      <IntegratedSearchNavBar
        currentCountry={schema}
        hideLocationPill
        feedFilterProps={{
          onFilterChange: handleFilterChange,
          currentSchema: schema,
          currentTable: table,
          currentFilterColumn: effectiveFilterColumn,
          currentFilterString: effectiveFilterString,
          currentCommittee: isCommitteeAPI ? committeeName : '',
          stateKey: filterStateKey,
          // This page renders one table from /feed/:schema/:table. Updates is
          // crawl-backed and has no table behind it — federally or per state —
          // so it's dropped from the Topic dropdown here rather than offered
          // and then silently ignored. Glance / Recents / Following are the
          // same class of page-owned view; add hideGlance / hideRecents /
          // hideFollowing here if they should go too.
          hideUpdates: true,
          data: data,
          currentOrderBy: effectiveOrderBy,
          allowedOrderBy: tableConfig?.allowedOrderBy || [],
          onSortChange: handleSortChange,
        }}
      />

      <FeedPageLayout {...layoutProps} />
    </>
  );
};

export default FullFeedPage;